import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdyenCheckout from '@adyen/adyen-web';
import type { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
import type DropinElement from '@adyen/adyen-web/dist/types/components/Dropin';
import type { PaymentMethod } from '@jwp/ott-common/types/checkout';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';
import classNames from 'classnames';

import Button from '../Button/Button';
import FormFeedback from '../FormFeedback/FormFeedback';

import styles from './Adyen.module.scss';
import './AdyenForm.scss';

import '@adyen/adyen-web/dist/adyen.css';

type Props = {
  configuration: CoreOptions;
  error?: string;
  selectedPaymentMethod: PaymentMethod | undefined;
  onPaymentMethodChange: (paymentMethod: string) => void;
  onPayPalSubmit: () => void;
  paymentMethods: PaymentMethod[];
};

const supportedExternalPaymentMethods = ['paypal'];

const Adyen: React.FC<Props> = ({ configuration, paymentMethods, selectedPaymentMethod, onPaymentMethodChange, onPayPalSubmit, error }) => {
  const { t } = useTranslation('account');
  const [isReady, setIsReady] = useState(false);
  const checkoutElementRef = useRef(null);
  const checkoutRef = useRef<DropinElement>(null);

  const externalPaymentMethods = paymentMethods.filter(
    (paymentMethod) => paymentMethod.paymentGateway !== 'adyen' && supportedExternalPaymentMethods.includes(paymentMethod.methodName),
  );
  const payPalPaymentMethod = externalPaymentMethods.find((paymentMethod) => paymentMethod.methodName === 'paypal');
  const isPayPalSelected = selectedPaymentMethod && selectedPaymentMethod.methodName === payPalPaymentMethod?.methodName;

  const handlePaymentMethodChange = useEventCallback(onPaymentMethodChange);

  const handleExternalPaymentMethodClick = (paymentMethod: PaymentMethod) => {
    checkoutRef.current?.closeActivePaymentMethod();
    handlePaymentMethodChange(paymentMethod.methodName);
  };

  useEffect(() => {
    if (!checkoutElementRef.current || !configuration.clientKey) {
      return;
    }

    const createCheckout = async () => {
      const checkout = await AdyenCheckout(configuration);

      if (checkoutElementRef.current) {
        checkoutRef.current = checkout.create('dropin', {
          onSelect: (method) => handlePaymentMethodChange(method.type),
          onReady: () => setIsReady(true),
        });
        checkoutRef.current?.mount(checkoutElementRef.current);
      }
    };

    createCheckout();

    return () => {
      checkoutRef.current?.unmount();
    };
  }, [configuration, handlePaymentMethodChange]);

  return (
    <div className={styles.adyen}>
      {error ? <FormFeedback variant="error">{error}</FormFeedback> : null}
      <div className={styles.container}>
        <div ref={checkoutElementRef} className={styles.adyenDropin} />
        {isReady && payPalPaymentMethod ? (
          <div className="adyen-checkout__dropin adyen-checkout__dropin--ready">
            <ul className="DropinComponent-module_adyen-checkout__payment-methods-list__mAjAm adyen-checkout__payment-methods-list">
              {payPalPaymentMethod ? (
                <li
                  className={classNames(
                    `adyen-checkout__payment-method DropinComponent-module_adyen-checkout__payment-method__nWdwg adyen-checkout__payment-method--ideal redirect-cf4e81c5-63dd-4843-9136-3fc1a83f35db`,
                    {
                      'adyen-checkout__payment-method--selected DropinComponent-module_adyen-checkout__payment-method--selected__6egZF': isPayPalSelected,
                    },
                  )}
                  onClick={() => handleExternalPaymentMethodClick(payPalPaymentMethod)}
                >
                  <div className="adyen-checkout__payment-method__header">
                    <button className="adyen-checkout__payment-method__header__title" role="radio" type="button" aria-checked={isPayPalSelected}>
                      <span
                        className={classNames('adyen-checkout__payment-method__radio', { 'adyen-checkout__payment-method__radio--selected': isPayPalSelected })}
                        aria-hidden="true"
                      ></span>
                      <span className="adyen-checkout__payment-method__image__wrapper DropinComponent-module_adyen-checkout__payment-method__image__wrapper__6NWzA adyen-checkout__payment-method__image__wrapper--outline">
                        <img
                          className="adyen-checkout__payment-method__image DropinComponent-module_adyen-checkout__payment-method__image__nB80V adyen-checkout__image adyen-checkout__image--loaded"
                          src={`https://checkoutshopper-test.adyen.com/checkoutshopper/images/logos/paypal.svg`}
                          alt={`PayPal logo`}
                        />
                      </span>
                      <span className="adyen-checkout__payment-method__name_wrapper">
                        <span
                          className={classNames(`adyen-checkout__payment-method__name`, { 'adyen-checkout__payment-method__name--selected': isPayPalSelected })}
                        >
                          {t('checkout.paypal')}
                        </span>
                      </span>
                    </button>
                  </div>
                  {isPayPalSelected ? (
                    <div className="adyen-checkout__payment-method__details">
                      <p className="adyen-checkout-form-instruction">{t('checkout.paypal_instructions')}</p>
                    </div>
                  ) : null}
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>
      <Button
        label={t('checkout.continue')}
        variant="contained"
        color="primary"
        size="large"
        onClick={() => {
          if (isPayPalSelected) {
            onPayPalSubmit();
          } else {
            checkoutRef.current?.submit();
          }
        }}
        fullWidth
      />
    </div>
  );
};

export default Adyen;
